import {
  useAuthenticateImpersonationTokenMutation,
  useValidateDiscoveryTokenMutation,
} from "@graphql/auth/auth.g";
import { StytchTokenTypeEnum } from "@graphql/generated/graphql.g";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Spinner } from "@design-system/Spinner/Spinner";

import { routes } from "@pages/routes";

import { STORAGE_KEYS } from "@utils/storageKeys";
import { setTenantSchemaTag } from "../../../sentry";

import classes from "./Authenticate.module.css";

export const Authenticate = () => {
  const [searchParams] = useSearchParams();
  const validateDiscoveryTokenMutation = useValidateDiscoveryTokenMutation();
  const authenticateImpersonationTokenMutation =
    useAuthenticateImpersonationTokenMutation();
  const navigate = useNavigate();

  const handleAuthentication = useCallback(async () => {
    try {
      if (
        validateDiscoveryTokenMutation.isPending ||
        validateDiscoveryTokenMutation.isSuccess ||
        authenticateImpersonationTokenMutation.isPending ||
        authenticateImpersonationTokenMutation.isSuccess
      )
        return;

      const token = searchParams.get("token");
      const stytchTokenType = searchParams
        .get("stytch_token_type")
        ?.toUpperCase() as StytchTokenTypeEnum;

      if (!token) {
        navigate(routes.login.index.$path(), {
          viewTransition: true,
          replace: true,
        });
        return;
      }

      if (stytchTokenType === StytchTokenTypeEnum.MultiTenantImpersonation) {
        const data = await authenticateImpersonationTokenMutation.mutateAsync({
          impersonationToken: token,
        });

        if (data.authenticateImpersonationToken?.organizationSlug) {
          setTenantSchemaTag(
            data.authenticateImpersonationToken.organizationSlug,
          );
          if (data.authenticateImpersonationToken?.impersonationSession) {
            sessionStorage.setItem("impersonation_session", "true");
          }

          navigate(routes.portal.dashboard.$path(), {
            viewTransition: true,
            replace: true,
          });
          return;
        }
      }

      if (!stytchTokenType) {
        sessionStorage.removeItem("impersonation_session");

        navigate(routes.login.index.$path(), {
          viewTransition: true,
          replace: true,
        });
        return;
      }

      const data = await validateDiscoveryTokenMutation.mutateAsync({
        token,
        stytchTokenType: stytchTokenType as StytchTokenTypeEnum,
      });

      const intermediateSessionToken =
        data.validateDiscoveryToken?.intermediateSessionToken;

      if (intermediateSessionToken) {
        sessionStorage.setItem(
          STORAGE_KEYS.INTERMEDIATE_SESSION_TOKEN,
          intermediateSessionToken || "",
        );

        sessionStorage.removeItem("impersonation_session");
      }

      const discoveredOrganizations =
        data.validateDiscoveryToken?.discoveredOrganizations;

      if (!discoveredOrganizations || discoveredOrganizations.length === 0) {
        navigate(routes.login.createOrganization.$path(), {
          viewTransition: true,
          replace: true,
        });
        return;
      } else {
        navigate(routes.login.organizations.$path(), {
          viewTransition: true,
          replace: true,
        });
      }
    } catch {
      navigate(routes.login.index.$path(), {
        viewTransition: true,
        replace: true,
      });
    }
  }, [
    navigate,
    searchParams,
    validateDiscoveryTokenMutation,
    authenticateImpersonationTokenMutation,
  ]);

  const debouncedHandleAuthentication = useMemo(
    () => debounce(handleAuthentication, 1000),
    [handleAuthentication],
  );

  useEffect(() => {
    debouncedHandleAuthentication();
  }, [debouncedHandleAuthentication]);

  return (
    <div className={classes.container}>
      <Spinner size={32} />
    </div>
  );
};
